/**
 * 火山虚拟人像库管理接口
 * 文档：https://docs.api.33dd.cn/zh/api/management/%E7%81%AB%E5%B1%B1%E8%99%9A%E6%8B%9F%E4%BA%BA%E5%83%8F%E5%BA%93
 * Base URL 与站内其它 AI 网关接口一致，走 REACT_APP_BASE_URL（如 /proxy/base）。
 */

import { del, get, post } from '../request';
import { PORTRAIT_GROUP_TYPE_AIGC } from '../utils/seedanceConstants';

const BASE_URL = process.env.REACT_APP_BASE_URL || '/proxy/base';

export interface VirtualPortraitRequestOptions {
  baseURL?: string;
  onDebug?: (message: string, isError?: boolean) => void;
  signal?: AbortSignal;
  /** 不填则从 localStorage `ss_system_token` 读取 */
  systemToken?: string;
  /** 不填则从 localStorage `ss_user_id` 读取 */
  userId?: string;
}

export interface VirtualPortraitCreateGroupBody {
  name: string;
  description: string;
  project_name: string;
}

export interface VirtualPortraitCreateAssetBody {
  group_id: string;
  url: string;
  name: string;
  /** 文档示例为 `"Image"`（大小写敏感，勿写 image） */
  asset_type: string;
}

/** 与其它 New-Api 接口一致：`Authorization` + `New-Api-User`。供 OSS 批量上传等同源网关复用。 */
export const buildVirtualPortraitAuthHeaders = (options: VirtualPortraitRequestOptions) => {
  const token = (
    options.systemToken ??
    (typeof window !== 'undefined' ? localStorage.getItem('ss_key') : '') ??
    ''
  ).trim();
  const userId = (
    options.userId ??
    (typeof window !== 'undefined' ? localStorage.getItem('ss_user_id') : '') ??
    ''
  ).trim();
  if (!token || !userId) {
    throw new Error('请先配置系统访问令牌和用户ID');
  }
  return {
    Authorization: `Bearer ${token.replace(/^Bearer\s+/i, '')}`,
    'New-Api-User': userId,
  } as const;
};

const vpRequestConfig = (options: VirtualPortraitRequestOptions) => {
  const baseURL = options.baseURL ?? BASE_URL;
  const headers = buildVirtualPortraitAuthHeaders(options);
  options.onDebug?.('Headers: Authorization, New-Api-User');
  return {
    baseURL,
    headers,
    responseType: 'json' as const,
  };
};

/** GET /api/virtual_portrait/group — 查询资产组列表 */
export const listVirtualPortraitGroups = <T = any>(
  projectName: string,
  options: VirtualPortraitRequestOptions,
) => {
  options.onDebug?.(`GET ${BASE_URL}/api/virtual_portrait/group?project_name=...`);
  return get<T>('/api/virtual_portrait/group', {
    ...vpRequestConfig(options),
    params: { project_name: projectName, group_type: PORTRAIT_GROUP_TYPE_AIGC },
    cache: 'no-store',
  });
};

/** POST /api/virtual_portrait/group — 创建资产组 */
export const createVirtualPortraitGroup = <T = any>(
  body: VirtualPortraitCreateGroupBody,
  options: VirtualPortraitRequestOptions,
) => {
  options.onDebug?.(`POST /api/virtual_portrait/group`);
  return post<T, VirtualPortraitCreateGroupBody>('/api/virtual_portrait/group', body, {
    ...vpRequestConfig(options),
    params: { group_type: PORTRAIT_GROUP_TYPE_AIGC },
  });
};

/** DELETE /api/virtual_portrait/group/:group_id — 删除资产组 */
export const deleteVirtualPortraitGroup = <T = any>(
  groupId: string,
  projectName: string,
  options: VirtualPortraitRequestOptions,
) => {
  const path = `/api/virtual_portrait/group/${encodeURIComponent(groupId)}`;
  options.onDebug?.(`DELETE ${path}?project_name=...`);
  return del<T>(path, {
    ...vpRequestConfig(options),
    params: { project_name: projectName, group_type: PORTRAIT_GROUP_TYPE_AIGC },
  });
};

export interface VirtualPortraitListQuery {
  /** 页码，从 1 开始（query：page_number） */
  page_number?: number;
  /** 每页数量（query：page_size） */
  page_size?: number;
  /** 按素材名称模糊搜索（ListAssets Filter.Name） */
  name?: string;
}

export interface ListAssetsRequestBody {
  Filter: {
    GroupType: string;
    GroupIds?: string[];
    Name?: string;
  };
  SortBy: 'CreateTime';
  SortOrder: 'Asc' | 'Desc';
  PageNumber?: number;
  PageSize?: number;
}

/** 组装 ListAssets 请求体。名称搜索必须走 Filter.Name，Filter.Title 会被上游忽略。 */
export const buildListAssetsRequestBody = (
  groupId?: string,
  listQuery?: VirtualPortraitListQuery,
): ListAssetsRequestBody => {
  const data: ListAssetsRequestBody = {
    Filter: {
      GroupType: PORTRAIT_GROUP_TYPE_AIGC,
    },
    SortBy: 'CreateTime',
    SortOrder: 'Desc',
  };
  if (groupId) {
    data.Filter.GroupIds = [groupId];
  }
  if (listQuery?.page_number != null && listQuery.page_number > 0) {
    data.PageNumber = Math.floor(listQuery.page_number);
  }
  if (listQuery?.page_size != null && listQuery.page_size > 0) {
    data.PageSize = Math.floor(listQuery.page_size);
  }
  const nameQuery = listQuery?.name?.trim();
  if (nameQuery) data.Filter.Name = nameQuery;
  return data;
};

/** POST ListAssets — 查询人像素材列表（可选按素材组、名称筛选）；禁缓存避免 304 */
export const listVirtualPortraits = <T = any>(
  options: VirtualPortraitRequestOptions,
  groupId?: string,
  listQuery?: VirtualPortraitListQuery,
) => {
  const data = buildListAssetsRequestBody(groupId, listQuery);

  options.onDebug?.(
    `POST /seedance/assets/?Action=ListAssets&Version=2024-01-01`,
  );
  return post<T>('/seedance/assets/?Action=ListAssets&Version=2024-01-01', data, {
    ...vpRequestConfig(options),
    cache: 'no-store',
    ...(options.signal ? { signal: options.signal } : {}),
  });
};

/** — 上传人像素材 */
export const createVirtualPortrait = async <T = any>(
  body: VirtualPortraitCreateAssetBody,
  options: VirtualPortraitRequestOptions,
) => {
  const path = `/seedance/assets/?Action=CreateAsset&Version=2024-01-01`;
  options.onDebug?.(`POST ${path}`);
  
  const data = {
    GroupId: body.group_id,
    URL: body.url,
    AssetType: body.asset_type,
    ...(body.name ? { Name: body.name } : {}),
  };

  const res = await post<any>(path, data, {
    ...vpRequestConfig(options),
  });

  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '上传素材失败');
  }
  
  return res as T;
};

/** — 获取素材详情 */
export const getVirtualPortrait = async <T = any>(
  id: string,
  options: VirtualPortraitRequestOptions,
) => {
  const assetId = id.trim();
  if (!assetId) {
    throw new Error('获取素材详情需要素材 ID');
  }
  const path = `/seedance/assets/?Action=GetAsset&Version=2024-01-01`;
  options.onDebug?.(`POST ${path}`);
  const data = {
    Id: assetId
  };
  const res = await post<any>(path, data, {
    ...vpRequestConfig(options),
    cache: 'no-store',
    ...(options.signal ? { signal: options.signal } : {}),
  });
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '获取素材详情失败');
  }
  return res as T;
};

/** — 删除人像素材 */
export const deleteVirtualPortrait = async <T = any>(
  assetId: string,
  options: VirtualPortraitRequestOptions,
) => {
  const path = `/seedance/assets/?Action=DeleteAsset&Version=2024-01-01`;
  options.onDebug?.(`POST ${path}`);
  const res = await post<any>(path, { Id: assetId }, {
    ...vpRequestConfig(options),
  });
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '删除素材失败');
  }
  return res as T;
};
